import { useEffect, useMemo, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
} from 'recharts';
import {
  ArrowDownRight,
  ArrowUpRight,
  ChevronLeft,
  ChevronRight,
  TrendingDown,
  TrendingUp,
  X,
} from 'lucide-react';
import { useServices } from '@/app/providers';
import { useI18n } from '@/shared/i18n';
import type { FinanceDashboardResponse, FinanceMovement } from '@/shared/types';

type MovementTypeFilter = 'all' | 'income' | 'expense';

type TranslateFn = (key: string, params?: Record<string, string | number>) => string;

const PAYMENT_METHOD_KEYS: Record<string, string> = {
  CASH: 'finance.method.cash',
  TRANSFER: 'finance.method.transfer',
  CARD: 'finance.method.card',
  CHECK: 'finance.method.check',
};

/**
 * A movement `tag` can be a payment method enum (income), a legacy expense
 * category enum, or an already-human configurable category name. Translate the
 * first two via i18n; leave configurable names untouched.
 */
function translateTag(tag: string | null, t: TranslateFn): string | null {
  if (!tag) return null;
  const methodKey = PAYMENT_METHOD_KEYS[tag];
  if (methodKey) return t(methodKey);
  if (/^[A-Z_]+$/.test(tag)) {
    const catKey = `admin.expenses.cat.${tag.toLowerCase()}`;
    const translated = t(catKey);
    if (translated !== catKey) return translated;
  }
  return tag;
}

function formatMXN(amount: number): string {
  if (Math.abs(amount) >= 1_000_000) return `$${(amount / 1_000_000).toFixed(2)}M`;
  if (Math.abs(amount) >= 1_000) return `$${(amount / 1_000).toFixed(1)}K`;
  return `$${amount.toLocaleString('es-MX')}`;
}

/** Parse a YYYY-MM into a local Date at the first of the month (avoids UTC off-by-one). */
function parseMonth(month: string): Date {
  const [year, mon] = month.split('-').map(Number);
  return new Date(year, mon - 1, 1);
}

function formatMonthLabel(month: string): string {
  return parseMonth(month).toLocaleDateString('es-MX', { month: 'long', year: 'numeric' });
}

function currentMonth(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

function shiftMonth(month: string, delta: number): string {
  const d = parseMonth(month);
  d.setMonth(d.getMonth() + delta);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function DeltaBadge({ pct }: { pct: number | null }) {
  if (pct === null) return null;
  const positive = pct >= 0;
  return (
    <span
      className={`inline-flex items-center gap-0.5 text-[11px] font-medium ${
        positive ? 'text-[#639922]' : 'text-[#E24B4A]'
      }`}
    >
      {positive ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
      {Math.abs(pct)}% vs. mes anterior
    </span>
  );
}

const WEEKDAYS = ['L', 'M', 'M', 'J', 'V', 'S', 'D'];

interface MonthCalendarProps {
  month: string;
  daily: FinanceDashboardResponse['daily'];
  selectedDay: string | null;
  onSelectDay: (day: string | null) => void;
  loading?: boolean;
}

function MonthCalendar({ month, daily, selectedDay, onSelectDay, loading }: MonthCalendarProps) {
  const byDate = useMemo(() => {
    const map = new Map<string, { income: number; expense: number }>();
    for (const point of daily) {
      map.set(point.date, { income: point.income, expense: point.expense });
    }
    return map;
  }, [daily]);

  const { cells, monthDate } = useMemo(() => {
    const monthDate = parseMonth(month);
    const year = monthDate.getFullYear();
    const mon = monthDate.getMonth();
    const daysInMonth = new Date(year, mon + 1, 0).getDate();
    // JS: 0=Sun..6=Sat → shift so Monday is index 0.
    const firstWeekday = (new Date(year, mon, 1).getDay() + 6) % 7;

    const cells: (string | null)[] = [];
    for (let i = 0; i < firstWeekday; i++) cells.push(null);
    for (let day = 1; day <= daysInMonth; day++) {
      cells.push(`${month}-${String(day).padStart(2, '0')}`);
    }
    return { cells, monthDate };
  }, [month]);

  const todayKey = new Date().toISOString().slice(0, 10);

  return (
    <div className="bg-white rounded-xl border-[0.5px] border-[#E8E6F8] p-5">
      <div className="flex items-center justify-between mb-3">
        <p className="text-[13px] font-medium text-[#2C2C2A] capitalize">
          {monthDate.toLocaleDateString('es-MX', { month: 'long', year: 'numeric' })}
        </p>
        {selectedDay && (
          <button
            onClick={() => onSelectDay(null)}
            className="text-[11px] text-[#7F77DD] font-medium inline-flex items-center gap-0.5 hover:underline"
          >
            <X size={11} /> Quitar filtro
          </button>
        )}
      </div>

      <div className="grid grid-cols-7 gap-1 mb-1">
        {WEEKDAYS.map((d, i) => (
          <div key={i} className="text-center text-[10px] font-medium text-[#888780] py-1">
            {d}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-1">
        {cells.map((dateKey, i) => {
          if (!dateKey) return <div key={`empty-${i}`} />;
          const data = byDate.get(dateKey);
          const hasIncome = (data?.income ?? 0) > 0;
          const hasExpense = (data?.expense ?? 0) > 0;
          const hasMovement = hasIncome || hasExpense;
          const dayNum = Number(dateKey.slice(-2));
          const isSelected = selectedDay === dateKey;
          const isToday = dateKey === todayKey;

          return (
            <button
              key={dateKey}
              disabled={loading || !hasMovement}
              onClick={() => onSelectDay(isSelected ? null : dateKey)}
              title={
                hasMovement
                  ? `Ingresos: ${formatMXN(data?.income ?? 0)} · Egresos: ${formatMXN(data?.expense ?? 0)}`
                  : undefined
              }
              className={`relative aspect-square rounded-lg flex flex-col items-center justify-center text-[12px] transition-colors ${
                isSelected
                  ? 'bg-[#7F77DD] text-white'
                  : hasMovement
                    ? 'bg-[#F0EFF9] text-[#2C2C2A] hover:bg-[#E1DEF7] cursor-pointer'
                    : 'text-[#B8B7B0] cursor-default'
              } ${isToday && !isSelected ? 'ring-1 ring-[#7F77DD]' : ''}`}
            >
              <span className={isToday && !isSelected ? 'font-bold' : ''}>{dayNum}</span>
              {hasMovement && (
                <span className="flex gap-0.5 mt-0.5 h-1">
                  {hasIncome && (
                    <span
                      className={`w-1 h-1 rounded-full ${isSelected ? 'bg-white' : 'bg-[#639922]'}`}
                    />
                  )}
                  {hasExpense && (
                    <span
                      className={`w-1 h-1 rounded-full ${isSelected ? 'bg-white' : 'bg-[#E24B4A]'}`}
                    />
                  )}
                </span>
              )}
            </button>
          );
        })}
      </div>

      <div className="flex items-center gap-3 mt-3 text-[10px] text-[#888780]">
        <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-[#639922]" /> Ingresos</span>
        <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-[#E24B4A]" /> Egresos</span>
      </div>
    </div>
  );
}

export function FinanceDashboardPanel() {
  const { financeDashboardRepo } = useServices();
  const { t } = useI18n();
  const [month, setMonth] = useState<string>(currentMonth());
  const [data, setData] = useState<FinanceDashboardResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState<MovementTypeFilter>('all');
  const [selectedDay, setSelectedDay] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setSelectedDay(null);
    financeDashboardRepo
      .getDashboard(month)
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [financeDashboardRepo, month]);

  const health = data?.health;
  const daily = data?.daily ?? [];
  const byCategory = data?.byCategory ?? [];

  const filteredMovements = useMemo(() => {
    let movements: FinanceMovement[] = data?.movements ?? [];
    if (typeFilter !== 'all') movements = movements.filter((m) => m.type === typeFilter);
    if (selectedDay) movements = movements.filter((m) => m.date === selectedDay);
    return movements;
  }, [data, typeFilter, selectedDay]);

  const chartData = useMemo(
    () =>
      daily.map((d) => ({
        ...d,
        label: new Date(`${d.date}T00:00:00`).toLocaleDateString('es-MX', { day: 'numeric', month: 'short' }),
      })),
    [daily]
  );

  const isCurrentMonth = month === currentMonth();

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold text-slate-800">Resumen financiero</h2>
          <p className="text-slate-500 capitalize">{formatMonthLabel(month)}</p>
        </div>

        {/* Month navigation */}
        <div className="flex items-center gap-1 bg-white rounded-lg border-[0.5px] border-[#E8E6F8] p-1">
          <button
            onClick={() => setMonth((m) => shiftMonth(m, -1))}
            className="w-8 h-8 rounded-md flex items-center justify-center text-slate-500 hover:bg-slate-100"
            aria-label="Mes anterior"
          >
            <ChevronLeft size={18} />
          </button>
          <span className="text-sm font-medium text-slate-700 capitalize min-w-[120px] text-center">
            {formatMonthLabel(month)}
          </span>
          <button
            onClick={() => setMonth((m) => shiftMonth(m, 1))}
            disabled={isCurrentMonth}
            className="w-8 h-8 rounded-md flex items-center justify-center text-slate-500 hover:bg-slate-100 disabled:opacity-30 disabled:hover:bg-transparent"
            aria-label="Mes siguiente"
          >
            <ChevronRight size={18} />
          </button>
        </div>
      </div>

      {/* Health cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border-[0.5px] border-[#E8E6F8] p-5">
          <div className="flex items-center justify-between mb-2">
            <p className="text-[11px] font-medium uppercase tracking-[0.5px] text-[#639922]">Ingresos del mes</p>
            <ArrowUpRight size={16} className="text-[#639922]" />
          </div>
          {loading ? (
            <div className="h-8 w-24 rounded bg-theme-200/50 animate-pulse" />
          ) : (
            <>
              <p className="text-2xl font-semibold text-[#2C2C2A]">{formatMXN(health?.income ?? 0)}</p>
              <DeltaBadge pct={health?.incomeDeltaPct ?? null} />
            </>
          )}
        </div>

        <div className="bg-white rounded-xl border-[0.5px] border-[#E8E6F8] p-5">
          <div className="flex items-center justify-between mb-2">
            <p className="text-[11px] font-medium uppercase tracking-[0.5px] text-[#E24B4A]">Egresos del mes</p>
            <ArrowDownRight size={16} className="text-[#E24B4A]" />
          </div>
          {loading ? (
            <div className="h-8 w-24 rounded bg-theme-200/50 animate-pulse" />
          ) : (
            <>
              <p className="text-2xl font-semibold text-[#2C2C2A]">{formatMXN(health?.expenses ?? 0)}</p>
              <DeltaBadge pct={health?.expenseDeltaPct ?? null} />
            </>
          )}
        </div>

        <div className="bg-white rounded-xl border-[0.5px] border-[#E8E6F8] p-5">
          <p className="text-[11px] font-medium uppercase tracking-[0.5px] text-[#7F77DD] mb-2">Balance neto</p>
          {loading ? (
            <div className="h-8 w-24 rounded bg-theme-200/50 animate-pulse" />
          ) : (
            <p
              className={`text-2xl font-semibold ${
                (health?.net ?? 0) >= 0 ? 'text-[#2C2C2A]' : 'text-[#E24B4A]'
              }`}
            >
              {formatMXN(health?.net ?? 0)}
            </p>
          )}
          <p className="text-[11px] text-[#888780] mt-1">Ingresos menos egresos</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Daily income/expense chart */}
        <div className="lg:col-span-2 bg-white rounded-xl border-[0.5px] border-[#E8E6F8] p-5">
          <div className="flex items-center justify-between mb-3">
            <p className="text-[13px] font-medium text-[#2C2C2A]">Ingresos vs. egresos por día</p>
            <div className="flex items-center gap-3 text-[11px] text-[#888780]">
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#639922]" /> Ingresos</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#E24B4A]" /> Egresos</span>
            </div>
          </div>
          {loading ? (
            <div className="h-[220px] rounded-lg bg-[#F0EFF9] animate-pulse" />
          ) : chartData.length === 0 ? (
            <div className="h-[220px] flex items-center justify-center">
              <p className="text-[13px] text-[#888780]">Sin movimientos este mes</p>
            </div>
          ) : (
            <div className="h-[220px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} barGap={2}>
                  <CartesianGrid vertical={false} stroke="rgba(0,0,0,0.05)" />
                  <XAxis dataKey="label" tick={{ fontSize: 9, fill: '#888780' }} axisLine={false} tickLine={false} interval="preserveStartEnd" />
                  <Tooltip
                    formatter={(v: number, name: string) => [formatMXN(v), name === 'income' ? 'Ingresos' : 'Egresos']}
                    contentStyle={{ fontSize: 11, borderRadius: 8, backgroundColor: '#2C2C2A', border: 'none', color: '#FFFFFF' }}
                    itemStyle={{ color: '#FFFFFF' }}
                    labelStyle={{ color: '#FFFFFF' }}
                  />
                  <Bar dataKey="income" fill="#639922" radius={[3, 3, 0, 0]} />
                  <Bar dataKey="expense" fill="#E24B4A" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        {/* Category breakdown */}
        <div className="bg-white rounded-xl border-[0.5px] border-[#E8E6F8] p-5">
          <p className="text-[13px] font-medium text-[#2C2C2A] mb-3">Egresos por categoría</p>
          {loading ? (
            <div className="space-y-2 animate-pulse">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="h-8 rounded-lg bg-[#F0EFF9]" />
              ))}
            </div>
          ) : byCategory.length === 0 ? (
            <p className="text-[13px] text-[#888780]">Sin egresos este mes</p>
          ) : (
            <div className="space-y-2.5">
              {byCategory.slice(0, 6).map((cat) => {
                const total = byCategory.reduce((s, c) => s + c.total, 0);
                const pct = total > 0 ? Math.round((cat.total / total) * 100) : 0;
                return (
                  <div key={cat.categoryId ?? cat.categoryName}>
                    <div className="flex justify-between text-[12px] mb-1">
                      <span className="flex items-center gap-1.5 text-[#2C2C2A]">
                        <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: cat.color ?? '#888780' }} />
                        {translateTag(cat.categoryName, t)}
                      </span>
                      <span className="font-medium text-[#2C2C2A]">{formatMXN(cat.total)}</span>
                    </div>
                    <div className="h-1.5 rounded-full bg-[#F0EFF9] overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: cat.color ?? '#888780' }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Calendar + recent movements */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <MonthCalendar
          month={month}
          daily={daily}
          selectedDay={selectedDay}
          onSelectDay={setSelectedDay}
          loading={loading}
        />

        <div className="lg:col-span-2 bg-white rounded-xl border-[0.5px] border-[#E8E6F8] p-5">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
            <p className="text-[13px] font-medium text-[#2C2C2A]">
              Movimientos recientes
              {selectedDay && (
                <span className="ml-1 text-[#7F77DD]">
                  · {new Date(`${selectedDay}T00:00:00`).toLocaleDateString('es-MX', { day: 'numeric', month: 'long' })}
                </span>
              )}
            </p>
            {/* Type filter */}
            <div className="flex items-center gap-1 bg-[#F0EFF9] rounded-lg p-0.5">
              {([
                { key: 'all', label: 'Todos' },
                { key: 'income', label: 'Ingresos' },
                { key: 'expense', label: 'Egresos' },
              ] as { key: MovementTypeFilter; label: string }[]).map((opt) => (
                <button
                  key={opt.key}
                  onClick={() => setTypeFilter(opt.key)}
                  className={`px-2.5 py-1 rounded-md text-[11px] font-medium transition-colors ${
                    typeFilter === opt.key ? 'bg-white text-[#2C2C2A] shadow-sm' : 'text-[#888780]'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {loading ? (
            <div className="space-y-2 animate-pulse">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="h-10 rounded-lg bg-[#F0EFF9]" />
              ))}
            </div>
          ) : filteredMovements.length === 0 ? (
            <p className="text-[13px] text-[#888780] py-6 text-center">
              {selectedDay || typeFilter !== 'all'
                ? 'No hay movimientos con estos filtros'
                : 'Sin movimientos este mes'}
            </p>
          ) : (
            <div className="divide-y divide-[#F0EFF9]">
              {filteredMovements.map((m) => (
                <div key={m.id} className="flex items-center justify-between py-2.5">
                  <div className="flex items-center gap-3 min-w-0">
                    <span
                      className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                        m.type === 'income' ? 'bg-green-50 text-[#639922]' : 'bg-red-50 text-[#E24B4A]'
                      }`}
                    >
                      {m.type === 'income' ? <ArrowUpRight size={16} /> : <ArrowDownRight size={16} />}
                    </span>
                    <div className="min-w-0">
                      <p className="text-[13px] font-medium text-[#2C2C2A] truncate">{m.concept}</p>
                      <p className="text-[11px] text-[#888780]">
                        {new Date(`${m.date}T00:00:00`).toLocaleDateString('es-MX', { day: 'numeric', month: 'short' })}
                        {(() => {
                          const label = translateTag(m.tag, t);
                          return label ? ` · ${label}` : '';
                        })()}
                      </p>
                    </div>
                  </div>
                  <span
                    className={`text-[13px] font-semibold flex-shrink-0 ${
                      m.type === 'income' ? 'text-[#639922]' : 'text-[#E24B4A]'
                    }`}
                  >
                    {m.type === 'income' ? '+' : '-'}{formatMXN(m.amount)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
